import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../utils/utils";
import useThemeColors from "../../hooks/useThemeColors";

import Tooltip from "@material-ui/core/Tooltip";

import {
  blackColor,
  dangerColor,
  grayColor,
  hexToRgb,
  whiteColor,
} from "../../assets/jss/material-dashboard-pro-react";

import {
  faChevronRight,
  faBackwardStep,
  faForwardStep,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ThreeDots } from "react-loader-spinner";
import HelpTooltip from "../HelpTooltip/HelpTooltip";

const spacing = 3;
const scaleFrom = 8;
const color1 = [118, 16, 15];
const color2 = [168, 23, 22];
const color3 = [201, 128, 126];
const color4 = [233, 233, 229];
const color5 = [164, 190, 191];
const color6 = [96, 147, 154];
const color7 = [27, 104, 116];

const styles = () => ({
  wrapper: {
    width: "100%",
    height: "100%",
    flexGrow: "1",
    backgroundColor: whiteColor,
    borderRadius: "2px",
    position: "relative",
  },
  pathDescription: {
    position: "absolute",
    top: "1px",
    left: "4px",
    textTransform: "uppercase",
    fontSize: "clamp(1.2vh, 12px, 1.5vh)",
    "& > div": {
      display: "inline-block",
      "& > span": {
        marginRight: "5px",
      },
    },
  },
  stepButton: {
    fontSize: "16px",
    padding: "0 5px",
    color: whiteColor,
    backgroundColor: grayColor[6],
    borderRadius: "2px",
    transition: "all 0.2s",
    display: "inline-block",
    "&.enabled": {
      cursor: "pointer",
      "&:hover": {
        // opacity: "0.7",
        boxShadow: "0 0 4px 1px rgba(" + hexToRgb(blackColor) + ", 0.4)",
        transform: "scale(1.05)",
      },
    },
    "&:not(.enabled)": {
      opacity: "0.1",
    },
    "&.back": {
      marginRight: "10px !important",
    },
    "&.forward": {
      marginLeft: "6px !important",
    },
  },
  pathDescriptionItem: {
    display: "inline-block",
    transform: "translateY(-1px)",
    lineHeight: "1.3",
    padding: "0",
    "&:not(.fade)": {
      fontWeight: "bold",
      boxShadow: "inset 0 -2px 0 0 " + grayColor[6],
    },
    "&.fade": {
      cursor: "pointer",
      // fontSize: "clamp(1.1vh, 11px, 1.4vh)",
      color: grayColor[9],
    },
  },
  pathDescriptionIcon: {
    fontSize: "14px",
  },
  pathDescriptionDivider: {
    color: grayColor[9],
    display: "inline-block",
    fontSize: "clamp(1.0vh, 10px, 1.2vh)",
    transform: "translateY(-1px)",
  },
  legend: {
    position: "absolute",
    bottom: "-3px",
    left: "4px",
    color: grayColor[0],
    fontSize: "clamp(1.2vh, 12px, 1.5vh)",
    "& > div": {
      display: "inline-block",
      "& > span": {
        marginRight: "5px",
      },
    },
  },
  colorLegend: {
    position: "absolute",
    top: "0",
    right: "4px",
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
  },
  scaleToggle: {
    display: "flex",
    borderRadius: "3px",
    overflow: "hidden",
    border: "1px solid " + grayColor[11],
    height: "12px",
  },
  scaleToggleButton: {
    padding: "0 5px",
    fontSize: "9px",
    fontWeight: "600",
    lineHeight: "12px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    color: grayColor[6],
    backgroundColor: whiteColor,
    "&:hover": {
      backgroundColor: grayColor[14],
    },
    "&.active": {
      backgroundColor: grayColor[6],
      color: whiteColor,
    },
  },
  colorLegendScale: {
    width: "144px",
    height: "12px",
    borderRadius: "3px",
    background: `linear-gradient(to right, rgb(${color1[0]},${color1[1]},${color1[2]}), rgb(${color2[0]},${color2[1]},${color2[2]}), rgb(${color3[0]},${color3[1]},${color3[2]}), rgb(${color4[0]},${color4[1]},${color4[2]}), rgb(${color5[0]},${color5[1]},${color5[2]}), rgb(${color6[0]},${color6[1]},${color6[2]}), rgb(${color7[0]},${color7[1]},${color7[2]}))`,
    position: "relative",
    "& > div": {
      position: "absolute",
      fontSize: "clamp(1.2vh, 12px, 1.5vh)",
      lineHeight: "1",
      marginTop: "14px",
      borderRadius: "3px",
    },
    "& > div:first-child": {
      textAlign: "left",
      left: "0",
      top: "50%",
      transform: "translate(0, -50%)",
    },
    "& > div:nth-child(2)": {
      textAlign: "center",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
    },
    "& > div:nth-child(3)": {
      textAlign: "right",
      left: "100%",
      top: "50%",
      transform: "translate(-100%, -50%)",
    },
  },
  valuesOmitted: {
    position: "absolute",
    top: "1px",
    right: "220px",
    color: grayColor[0],
    fontSize: "clamp(1.2vh, 12px, 1.5vh)",
  },
  canvas: {
    backgroundColor: grayColor[13],
    position: "absolute",
    left: "1px",
    right: "1px",
    top: "31px",
    bottom: "1px",
    cursor: "pointer",
  },
  noDataNotice: {
    position: "absolute",
    fontSize: "20px",
    fontStyle: "italic",
    textAlign: "center",
    width: "100%",
    top: "29%",
    transform: "translate(0, -50%)",
    color: grayColor[0],
    zIndex: 1,
  },
  noDataNoticeArrow: {
    position: "absolute",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1,
    "&.arrow1": {
      top: "16%",
      animationDelay: "0.5s",
    },
    "&.arrow2": {
      top: "18%",
      animationDelay: "0.3s",
    },
    "&.arrow3": {
      top: "20%",
      animationDelay: "0.1s",
    },
    animation: "$arrow-fade 1.5s infinite ease-in-out",
  },
  "@keyframes arrow-fade": {
    "0%": {
      opacity: "0.7",
    },
    "50%": {
      opacity: "0.1",
    },
    "100%": {
      opacity: "0.7",
    },
  },
  box: {
    position: "absolute",
  },
  parent: {
    zIndex: "1",
    outline: "2px solid white",
  },
  child: {
    userSelect: "none",
    fontSize: "clamp(1.2vh, 0.8rem, 1.5vh)",
    padding: "0.4vh",
    zIndex: "2",
    outline: "1px solid #ffffff88",
    transition: "opacity 0.15s",
    "&.canvasHover.parentHover:not(.childHover)": {
      opacity: "0.75",
    },
    "&.canvasHover:not(.parentHover):not(.childHover)": {
      opacity: "0.2",
    },
  },
  boxName: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    "&.white": {
      color: whiteColor,
    },
  },
  boxValue: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "0.85em",
    opacity: 0.85,
    fontWeight: 500,
    letterSpacing: "0.02em",
    "&.white": {
      color: whiteColor,
    },
  },
  mouseOverInfo: {
    position: "absolute",
    padding: "4px 6px",
    backgroundColor: whiteColor,
    opacity: "0.85",
    fontSize: "12px",
    lineHeight: "1.4",
    minWidth: "200px",
    zIndex: "10000",
    pointerEvents: "none",
    borderRadius: "2px",
    transition: "opacity 0.1s",
    "&.hidden": {
      opacity: "0",
    },
  },
  mouseOverValue: {
    fontWeight: "bold",
    "&.negative": {
      color: dangerColor[0],
    },
  },
  hidden: {
    visibility: "hidden",
  },
  clickedBox: {
    position: "absolute",
    zIndex: "3",
    borderRadius: "20%",
    transform: "scale(0)",
    animation: "$ripple-animation 600ms cubic-bezier(0,.6,.55,.95)",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    pointerEvents: "none",
  },
  "@keyframes ripple-animation": {
    to: {
      transform: "scale(1.0)",
      borderRadius: "0",
      opacity: "0",
    },
  },
  parentTag: {
    position: "absolute",
    fontSize: "12px",
    lineHeight: "1",
    padding: "2px",
    transform: "translate(0, calc(-100% - 1px))",
    borderRadius: "2px",
    color: whiteColor,
    backgroundColor: blackColor,
    zIndex: "4",
    opacity: "0.8",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    pointerEvents: "none",
  },
  selectedBox: {
    position: "absolute",
    zIndex: "500",
    pointerEvents: "none",
    "& > div": {
      position: "relative",
      width: "100%",
      height: "100%",
      "& > span": {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: whiteColor + "44",
      },
    },
  },
});
const useStyles = makeStyles(styles);

const Treemap = (props) => {
  const { clientColor } = useThemeColors();
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const {
    isLoading,
    levels,
    dimensionNames,
    setSelection,
    setPnlSelection,
    levelsCount,
    parentLevel,
    setParentLevel,
    childrenLevel,
    data,
    totalNodes,
    totalSize,
    colorMetric,
    scaleMode,
    globalStats,
    localStats,
    invertColors,
    selectedBox,
    setSelectedBox,
    valueNames,
    showValues,
  } = props;

  const dataRef = useRef([]);
  const [treemap, setTreemap] = useState([]);

  const canvasRef = useRef(null);
  const mouseOverRef = useRef(null);
  const [mouseOverInfo, setMouseOverInfo] = useState(null);
  const [mouseOverPosition, setMouseOverPosition] = useState({ x: 0, y: 0 });
  const [mouseMoving, setMouseMoving] = useState(false);
  const mouseMovingTimer = useRef(null);
  const [canvasHover, setCanvasHover] = useState(false);
  const valuesOmitted = useRef(false);
  const [clickedBox, setClickedBox] = useState({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [showClickedBox, setShowClickedBox] = useState(false);
  const [parentTagPos, setParentTagPos] = useState({ x: 0, y: 0 });

  const getColorPercentiles = () => {
    // Choose stats based on scale mode
    const stats = scaleMode === "local" ? localStats : globalStats;
    const metricStats =
      colorMetric === "per_volume" ? stats.valuePerVolume : stats.serveMargin;

    // Use real percentiles from the data
    const p5 = metricStats.p5 || 0;
    const p50 = metricStats.p50 || 0;
    const p95 = metricStats.p95 || 0;
    const weightedMean = metricStats.weightedMean || p50;

    // Use weightedMean as the neutral point (center of color scale)
    // This ensures the legend matches the actual colors displayed
    const neutralPoint = weightedMean;

    // Original scale boundaries
    let min = p5;
    let max = p95;

    // Handle skewed distributions where the weighted mean falls outside [p5, p95].
    // This happens when most elements have zero/low values but a few large elements
    // drive the weighted mean significantly higher (or lower) than the percentiles.
    // In this case, expand the deficient side using the other side's distance.
    if (max < neutralPoint) {
      // p95 is below the weighted mean — expand max to mirror the min-side distance
      max = neutralPoint + (neutralPoint - min);
    } else if (min > neutralPoint) {
      // p5 is above the weighted mean — expand min to mirror the max-side distance
      min = neutralPoint - (max - neutralPoint);
    }

    // Make the scale symmetric around the neutral point (weightedMean)
    // Find the smaller distance from neutral to the extremes, and adjust the larger one
    const distToMin = neutralPoint - min;
    const distToMax = max - neutralPoint;
    const smallerDist = Math.min(distToMin, distToMax);

    // Only apply symmetry if we have a valid range
    if (smallerDist > 0) {
      min = neutralPoint - smallerDist;
      max = neutralPoint + smallerDist;
    }

    // Calculate symmetric intermediate quartiles by linear interpolation
    // This ensures q1-q2-q3-q4-q5 are evenly spaced between min and max
    const q3 = neutralPoint; // weighted mean is the neutral point (gray color)
    const fallbackRange = Math.max(distToMin, distToMax, 0.001);
    const halfRange = smallerDist > 0 ? smallerDist : fallbackRange;

    // Symmetric quartiles: divide each half into 3 equal parts (for 7 colors)
    const q1 = q3 - (2 / 3) * halfRange;
    const q2 = q3 - (1 / 3) * halfRange;
    const q4 = q3 + (1 / 3) * halfRange;
    const q5 = q3 + (2 / 3) * halfRange;

    return { min, q1, q2, q3, q4, q5, max, weightedMean };
  };

  // Format large numbers for the scale legend (value/volume)
  const formatScaleValue = (value) => {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (abs >= 1e6) {
      return sign + (abs / 1e6).toFixed(1) + "M";
    } else if (abs >= 1e3) {
      return sign + (abs / 1e3).toFixed(1) + "K";
    } else if (abs >= 1) {
      return sign + abs.toFixed(1);
    } else {
      return sign + abs.toFixed(2);
    }
  };

  const [colorPercentiles, setColorPercentiles] = useState(() => {
    const { min, q1, q2, q3, q4, q5, max, weightedMean } =
      getColorPercentiles();
    return { min, q1, q2, q3, q4, q5, max, weightedMean };
  });
  const dataHasChildren = useRef(
    data.some((obj) => Object.prototype.hasOwnProperty.call(obj, "children")),
  );

  const calculateTreemap = (data = dataRef.current) => {
    const colorPercentiles = getColorPercentiles();

    // region size functions
    const getTotalSize = (arr, positiveOnly = false) => {
      return arr.reduce((sum, item) => {
        if (positiveOnly) {
          return sum + Math.max(item.size || 0, 0);
        } else {
          return sum + (item.size || 0);
        }
      }, 0);
    };
    const getOrientation = (canvas) => {
      return canvas.x1 - canvas.x0 > canvas.y1 - canvas.y0 ? "left" : "top";
    };
    const getNecessarySize = (canvas) => {
      let offset = 0.3 - ((0.3 - 0.15) * Math.min(canvas.n, 250)) / 250;
      return canvas.s * offset;
    };
    const getBox = (
      canvas,
      orientation,
      size,
      nodeGroupTotalSize,
      nodeGroupConsumed,
      nodeGroupParticipationOnCanvasRemaining,
      isChild,
    ) => {
      let x, y, width, height;
      if (orientation === "left") {
        let addedX = isChild || canvas.x0 === 0 ? 0 : spacing / 2;
        let addedY =
          isChild || (canvas.y0 === 0 && nodeGroupConsumed === 0)
            ? 0
            : spacing / 2;
        x = canvas.x0 + addedX;
        y =
          canvas.y0 +
          (nodeGroupConsumed / nodeGroupTotalSize) * (canvas.y1 - canvas.y0) +
          addedY;
        width =
          nodeGroupParticipationOnCanvasRemaining * (canvas.x1 - canvas.x0) -
          (isChild ? 0 : spacing / 2 + addedX);
        height =
          (size / nodeGroupTotalSize) * (canvas.y1 - canvas.y0) -
          (isChild ? 0 : spacing / 2 + addedY);
      } else {
        let addedX =
          isChild || (canvas.x0 === 0 && nodeGroupConsumed === 0)
            ? 0
            : spacing / 2;
        let addedY = isChild || canvas.y0 === 0 ? 0 : spacing / 2;
        x =
          canvas.x0 +
          (nodeGroupConsumed / nodeGroupTotalSize) * (canvas.x1 - canvas.x0) +
          addedX;
        y = canvas.y0 + addedY;
        width =
          (size / nodeGroupTotalSize) * (canvas.x1 - canvas.x0) -
          (isChild ? 0 : spacing / 2 + addedX);
        height =
          nodeGroupParticipationOnCanvasRemaining * (canvas.y1 - canvas.y0) -
          (isChild ? 0 : spacing / 2 + addedY);
      }
      return { x, y, width, height };
    };
    // endregion

    // region color functions
    const interpolateColor = (color1, color2, factor) => {
      const result = color1.slice();
      for (let i = 0; i < 3; i++) {
        result[i] = Math.round(result[i] + factor * (color2[i] - result[i]));
      }
      return result;
    };
    function getColor(value, min, q1, q2, q3, q4, q5, max) {
      // When invertColors is true (cost lines), swap color palette
      // so higher values = red (bad) and lower values = green (good)
      const c1 = invertColors ? color7 : color1;
      const c2 = invertColors ? color6 : color2;
      const c3 = invertColors ? color5 : color3;
      const c4 = color4; // neutral stays the same
      const c5 = invertColors ? color3 : color5;
      const c6 = invertColors ? color2 : color6;
      const c7 = invertColors ? color1 : color7;

      let color;
      if (value <= min) {
        color = c1;
      } else if (value >= max) {
        color = c7;
      } else if (value <= q1) {
        const factor = (value - min) / (q1 - min);
        color = interpolateColor(c1, c2, factor);
      } else if (value <= q2) {
        const factor = (value - q1) / (q2 - q1);
        color = interpolateColor(c2, c3, factor);
      } else if (value <= q3) {
        const factor = (value - q2) / (q3 - q2);
        color = interpolateColor(c3, c4, factor);
      } else if (value <= q4) {
        const factor = (value - q3) / (q4 - q3);
        color = interpolateColor(c4, c5, factor);
      } else if (value <= q5) {
        const factor = (value - q4) / (q5 - q4);
        color = interpolateColor(c5, c6, factor);
      } else {
        const factor = (value - q5) / (max - q5);
        color = interpolateColor(c6, c7, factor);
      }
      return { r: color[0], g: color[1], b: color[2] };
    }
    // endregion

    let boxes = [];
    const calculate = (canvas, data, parent) => {
      let dataLeft = structuredClone(data);
      let totalSize = getTotalSize(dataLeft);
      if (totalSize <= 0) return [];
      let totalPositiveSize = getTotalSize(dataLeft, true);
      let canvasState = {
        x0: canvas.x,
        y0: canvas.y,
        x1: canvas.width + canvas.x,
        y1: canvas.height + canvas.y,
        s: totalPositiveSize,
        n: data.length,
      };
      let nodeGroup = [];
      let nodeGroupTotalSize = 0;
      let nodeGroupConsumed = 0;
      let nodeGroupParticipationOnCanvasRemaining = 0;
      let orientation;
      while (dataLeft.length > 0) {
        let node = dataLeft[0];
        if (node.size > 0) {
          if (!nodeGroup.includes(node.name)) {
            // create node group
            orientation = getOrientation(canvasState);
            // let sizeNecessaryToSquare = getSizeNecessaryToSquare(canvasLeft[currentCanvas])
            let sizeNecessary = getNecessarySize(canvasState);
            let sizeSum = 0;
            for (let i = 0; i < dataLeft.length; i++) {
              if (dataLeft[i].size > 0) {
                nodeGroup.push(dataLeft[i].name);
                sizeSum += dataLeft[i].size;
                if (sizeSum >= sizeNecessary) {
                  nodeGroupTotalSize = sizeSum;
                  break;
                }
              }
            }
            nodeGroupParticipationOnCanvasRemaining =
              nodeGroupTotalSize / canvasState.s;
            nodeGroupConsumed = 0;
          }
          let box = getBox(
            canvasState,
            orientation,
            node.size,
            nodeGroupTotalSize,
            nodeGroupConsumed,
            nodeGroupParticipationOnCanvasRemaining,
            parent !== null || !dataHasChildren.current,
          );
          const nodeColorValue =
            colorMetric === "serve_margin"
              ? node.serve_margin_color
              : node.value_per_volume_color;
          const parentColorValue = parent
            ? colorMetric === "serve_margin"
              ? parent.serve_margin_color
              : parent.value_per_volume_color
            : null;

          box = {
            ...box,
            parent: parent === null && dataHasChildren.current,
            rgb: getColor(
              nodeColorValue,
              colorPercentiles.min,
              colorPercentiles.q1,
              colorPercentiles.q2,
              colorPercentiles.q3,
              colorPercentiles.q4,
              colorPercentiles.q5,
              colorPercentiles.max,
            ),
            name:
              node.name != null && node.name !== ""
                ? node.name
                : "● " + t("results.blank") + " ●",
            size: node.size,
            color: nodeColorValue,
            parentName:
              parent === null
                ? null
                : parent.name != null && parent.name !== ""
                  ? parent.name
                  : "● " + t("results.blank") + " ●",
            parentSize: parent?.size,
            parentColor: parentColorValue,
          };
          boxes.push(box);
          if (nodeGroup.indexOf(node.name) < nodeGroup.length - 1) {
            nodeGroupConsumed += node.size;
          } else {
            // close node group
            nodeGroup = [];
            if (orientation === "left") {
              canvasState.x0 +=
                nodeGroupParticipationOnCanvasRemaining *
                (canvasState.x1 - canvasState.x0);
            } else {
              canvasState.y0 +=
                nodeGroupParticipationOnCanvasRemaining *
                (canvasState.y1 - canvasState.y0);
            }
          }
          canvasState.s -= node.size;
          if (node.children) {
            calculate(box, node.children, node);
          }
        }
        dataLeft.shift();
      }
    };
    const canvasRect = canvasRef.current.getBoundingClientRect();
    let canvas = {
      width: canvasRect.width,
      height: canvasRect.height,
      x: 0,
      y: 0,
    };
    calculate(canvas, data, null);
    return boxes;
  };

  const handleResizeRef = useRef(null);
  handleResizeRef.current = () => {
    setTreemap(calculateTreemap(dataRef.current));
  };
  useEffect(() => {
    i18n.changeLanguage(i18n.language.slice(0, 2));
    const onResize = () => handleResizeRef.current();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);
  useEffect(() => {
    setTreemap(calculateTreemap(dataRef.current));
  }, [
    i18n.language,
    colorMetric,
    scaleMode,
    globalStats,
    localStats,
    invertColors,
  ]);
  useEffect(() => {
    dataRef.current = data; // for further use in handleSize event
    const { min, q1, q2, q3, q4, q5, max, weightedMean } =
      getColorPercentiles();
    setColorPercentiles({ min, q1, q2, q3, q4, q5, max, weightedMean });
    dataHasChildren.current = data.some((obj) =>
      Object.prototype.hasOwnProperty.call(obj, "children"),
    );
    setTreemap(calculateTreemap());
    valuesOmitted.current = totalNodes.current >= 250;
  }, [data, colorMetric, scaleMode, globalStats, localStats, invertColors]);
  useEffect(() => {
    // place the selected boxes accordingly (update positions when treemap changes)
    if (
      selectedBox.display &&
      selectedBox.items &&
      selectedBox.items.length > 0
    ) {
      setSelectedBox((draft) => {
        draft.items.forEach((item, idx) => {
          for (let i = 0; i < treemap.length; i++) {
            if (
              treemap[i].name === item.childrenName &&
              treemap[i].parentName === item.parentName
            ) {
              draft.items[idx].x = treemap[i].x;
              draft.items[idx].y = treemap[i].y;
              draft.items[idx].width = treemap[i].width;
              draft.items[idx].height = treemap[i].height;
              break;
            }
          }
        });
      });
    }
    // set the mouse over with the new treemap
    if (canvasHover) {
      const canvas_posX = mouseOverPosition.x - 20;
      const canvas_posY = mouseOverPosition.y - 50;
      treemap.forEach((box) => {
        if (
          canvas_posX >= box.x &&
          canvas_posX <= box.x + box.width &&
          canvas_posY >= box.y &&
          canvas_posY <= box.y + box.height
        ) {
          let { name, size, color, parentName, parentSize, parentColor } = box;
          setMouseOverInfo({
            name,
            size,
            color,
            parentName,
            parentSize,
            parentColor,
          });
        }
      });
    }
  }, [treemap]);

  const handleCanvasMouseOver = () => {
    setCanvasHover(true);
  };

  const handleBoxMouseOver = (e, box) => {
    let { name, size, color, parentName, parentSize, parentColor } = box;
    setMouseOverInfo({
      name,
      size,
      color,
      parentName,
      parentSize,
      parentColor,
    });
    if (parentName) {
      setParentTagPos(getParentPos(parentName));
    }
  };
  const handleBoxMouseMove = (e) => {
    if (mouseOverInfo) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const mouseOverInfoRect = mouseOverRef.current.getBoundingClientRect();
      let newPosX = e.pageX - canvasRect.left + window.scrollX + 20;
      let newPosY = e.pageY - canvasRect.top + window.scrollY + 50;
      let offsetX = 0,
        offsetY = 0;
      if (
        e.pageX + window.scrollX + mouseOverInfoRect.width + 20 >=
        canvasRect.right
      ) {
        offsetX = -mouseOverInfoRect.width - 30;
      }
      if (
        e.pageY + window.scrollY + mouseOverInfoRect.height + 20 >=
        canvasRect.bottom
      ) {
        offsetY = -mouseOverInfoRect.height - 20;
      }
      setMouseOverPosition({ x: newPosX + offsetX, y: newPosY + offsetY });
      setMouseMoving(true);
      clearTimeout(mouseMovingTimer.current);
      mouseMovingTimer.current = setTimeout(() => {
        setMouseMoving(false);
      }, 300);
    }
  };
  const setMouseOutInfo = () => {
    setCanvasHover(false);
    setMouseOverInfo(null);
  };

  const handleBoxOnClick = (e) => {
    let childrenName = mouseOverInfo.name;
    let parentName = mouseOverInfo.parentName;
    const isCtrlPressed = e.ctrlKey || e.metaKey; // metaKey for Mac
    const isAtLowestLevel = !(levelsCount.current > parentLevel + 1);

    // Get box position
    let x, y, width, height;
    for (let i = 0; i < treemap.length; i++) {
      if (
        treemap[i].name === childrenName &&
        treemap[i].parentName === parentName
      ) {
        x = treemap[i].x;
        y = treemap[i].y;
        width = treemap[i].width;
        height = treemap[i].height;
        break;
      }
    }

    const newItem = { x, y, width, height, parentName, childrenName };

    if (isCtrlPressed && isAtLowestLevel && selectedBox.display) {
      // Multi-selection: add to existing selection
      const dimension = levels[parentLevel];
      setPnlSelection((draft) => {
        const current = draft[dimension];
        const currentArray = Array.isArray(current) ? current : [current];
        // Toggle: if already selected, remove it; otherwise add it
        if (currentArray.includes(childrenName)) {
          const filtered = currentArray.filter((n) => n !== childrenName);
          if (filtered.length === 0) {
            delete draft[dimension];
          } else {
            draft[dimension] = filtered;
          }
        } else {
          draft[dimension] = [...currentArray, childrenName];
        }
      });

      setSelectedBox((draft) => {
        // Toggle: if already in items, remove it; otherwise add it
        const existingIndex = draft.items.findIndex(
          (item) =>
            item.childrenName === childrenName &&
            item.parentName === parentName,
        );
        if (existingIndex >= 0) {
          draft.items.splice(existingIndex, 1);
          if (draft.items.length === 0) {
            draft.display = false;
          }
        } else {
          draft.items.push(newItem);
        }
      });
    } else {
      // Single selection (normal click)
      setPnlSelection((draft) => {
        draft[levels[parentLevel]] = [childrenName];
      });

      if (!isAtLowestLevel) {
        setSelection((draft) => {
          draft[levels[parentLevel]] = childrenName;
        });
        setParentLevel((curLevel) => curLevel + 1);
      } else {
        setSelectedBox((draft) => {
          draft.display = true;
          draft.items = [newItem];
        });
      }
    }

    // visual click effect
    setClickedBox({ x, y, width, height });
    setShowClickedBox(true);
    setTimeout(() => {
      setShowClickedBox(false);
    }, 600);
  };
  const handleReturnLevelClick = (step) => {
    if (parentLevel <= 0) return;
    const elements_being_deleted = Object.values(levels)
      .slice(parentLevel - step, Object.values(levels).length)
      .filter((val) => val !== null);
    setSelection((draft) => {
      elements_being_deleted.forEach((el) => {
        delete draft[el];
      });
    });
    setPnlSelection((draft) => {
      elements_being_deleted.forEach((el) => {
        delete draft[el];
      });
    });
    setParentLevel((curLevel) => curLevel - step);
  };
  const handleAdvanceLevelClick = (step) => {
    if (parentLevel === levelsCount.current - step) return;
    setParentLevel((curLevel) => curLevel + step);
  };

  const treemapBox = (box, key) => {
    return (
      <div
        key={key}
        id={`box${key}`}
        className={cx({
          [classes.box]: true,
          [classes.parent]: box.parent,
          [classes.child]: !box.parent,
          ["childHover"]:
            mouseOverInfo &&
            (mouseOverInfo.parentName || !dataHasChildren.current) &&
            (mouseOverInfo.parentName === box.parentName ||
              !dataHasChildren.current) &&
            mouseOverInfo.name === box.name,
          ["parentHover"]:
            (mouseOverInfo &&
              mouseOverInfo.parentName &&
              mouseOverInfo.parentName === box.parentName) ||
            !dataHasChildren.current,
          ["canvasHover"]: canvasHover,
        })}
        style={{
          left: `${box.x}px`,
          top: `${box.y}px`,
          width: `${box.width}px`,
          height: `${box.height}px`,
          transform: `translateX(-${(100 * (1 - Math.min(box.width / scaleFrom, 1))) / 2}%) scaleX(${Math.min(box.width / scaleFrom, 1)}) translateY(-${(100 * (1 - Math.min(box.height / scaleFrom, 1))) / 2}%) scaleY(${Math.min(box.height / scaleFrom, 1)})`,
          backgroundColor: box.parent
            ? "white"
            : `rgb(${box.rgb.r}, ${box.rgb.g}, ${box.rgb.b})`,
        }}
        onMouseOver={(e) => handleBoxMouseOver(e, box)}
        onMouseMove={(e) => handleBoxMouseMove(e)}
        onClick={(e) => handleBoxOnClick(e)}
      >
        {!box.parent && box.width >= 30 && box.height >= 30 && (
          <div
            className={cx({
              [classes.boxName]: true,
              ["white"]: (box.rgb.r + box.rgb.g + box.rgb.b) / 3 < 168,
            })}
            style={{
              width: box.width,
            }}
          >
            {box.name}
          </div>
        )}
        {showValues && !box.parent && box.width >= 70 && box.height >= 45 && (
          <div
            className={cx({
              [classes.boxValue]: true,
              ["white"]: (box.rgb.r + box.rgb.g + box.rgb.b) / 3 < 168,
            })}
            style={{
              width: box.width,
            }}
          >
            {colorMetric === "per_volume"
              ? formatScaleValue(box.color)
              : (100 * box.color).toFixed(1) + "%"}
          </div>
        )}
      </div>
    );
  };
  const getParentPos = (parentName) => {
    for (let i = 0; i < treemap.length; i++) {
      if (treemap[i].name === parentName) {
        return { x: treemap[i].x, y: treemap[i].y };
      }
    }
  };

  // region Elements
  const spinner = isLoading && (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <ThreeDots width={"120px"} color={clientColor} ariaLabel="loading" />
    </div>
  );

  const pathDescription = (
    <div
      className={cx({
        [classes.pathDescription]: true,
        // [classes.hidden]: isLoading,
      })}
    >
      <div>
        <Tooltip
          placement={"top"}
          title={t("treemap.back_one_level")}
          PopperProps={{
            style: { marginBottom: "-8px" },
          }}
        >
          <span
            className={cx({
              [classes.stepButton]: true,
              ["back"]: true,
              ["enabled"]: parentLevel > 0 && !isLoading,
            })}
            onClick={() => handleReturnLevelClick(1)}
          >
            <FontAwesomeIcon icon={faBackwardStep} />
          </span>
        </Tooltip>
      </div>
      {Object.values(levels)
        .filter((val) => val !== null)
        .map((level, key) => {
          return (
            <div key={key}>
              <span
                className={cx({
                  [classes.pathDescriptionItem]: true,
                  ["fade"]: parentLevel !== key,
                })}
                onClick={
                  parentLevel > key
                    ? () => handleReturnLevelClick(parentLevel - key)
                    : parentLevel < key
                      ? () => handleAdvanceLevelClick(key - parentLevel)
                      : null
                }
              >
                {dimensionNames[level][i18n.language]}
              </span>
              {key + 1 < levelsCount.current && (
                <span
                  className={cx({
                    [classes.pathDescriptionDivider]: true,
                  })}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </span>
              )}
            </div>
          );
        })}
      <div>
        <Tooltip
          placement={"top"}
          title={t("treemap.forward_one_level")}
          PopperProps={{
            style: { marginBottom: "-8px" },
          }}
        >
          <span
            className={cx({
              [classes.stepButton]: true,
              ["forward"]: true,
              ["enabled"]: parentLevel < levelsCount.current - 1 && !isLoading,
            })}
            onClick={() => handleAdvanceLevelClick(1)}
          >
            <FontAwesomeIcon icon={faForwardStep} />
          </span>
        </Tooltip>
      </div>
    </div>
  );

  const colorLegend = data.length > 0 && (
    <div
      className={cx({
        [classes.colorLegend]: true,
        [classes.hidden]: isLoading,
      })}
    >
      {/* Color Scale Bar */}
      <Tooltip
        placement="top"
        title={
          <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
            <div>
              {colorMetric === "per_volume"
                ? t("treemap.metric_value_per_volume")
                : t("treemap.metric_serve_margin")}
            </div>
            <div>
              {scaleMode === "global"
                ? t("treemap.scale_range_percentile_global")
                : t("treemap.scale_range_percentile_local")}
            </div>
          </div>
        }
      >
        <div
          className={cx({
            [classes.colorLegendScale]: true,
          })}
          style={
            invertColors
              ? {
                  background: `linear-gradient(to right, rgb(${color7}), rgb(${color6}), rgb(${color5}), rgb(${color4}), rgb(${color3}), rgb(${color2}), rgb(${color1}))`,
                }
              : undefined
          }
        >
          {(() => {
            // Numbers always go min→max (left→right).
            // Only the gradient colors are inverted via the style above.
            const lo = colorPercentiles.min;
            const hi = colorPercentiles.max;
            const mid = colorPercentiles.weightedMean;
            if (colorMetric === "per_volume") {
              return (
                <>
                  <div>{formatScaleValue(lo)}</div>
                  <div>{formatScaleValue(mid)}</div>
                  <div>{formatScaleValue(hi)}</div>
                </>
              );
            }
            return (
              <>
                <div>{(100 * lo).toFixed(1)}%</div>
                <div>{(100 * mid).toFixed(1)}%</div>
                <div>{(100 * hi).toFixed(1)}%</div>
              </>
            );
          })()}
        </div>
      </Tooltip>
    </div>
  );

  const noDataNotice = data.length === 0 && (
    <>
      {[...Array(3).keys()].map((key) => {
        return (
          <div
            key={key}
            className={cx({
              [classes.noDataNoticeArrow]: true,
              ["arrow" + (key + 1)]: true,
              [classes.hidden]: isLoading,
            })}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 100 100"
              width="100px"
              height="30px"
              preserveAspectRatio="none"
              fill={clientColor}
            >
              <polygon points="50,0 100,93 100,100 50,7 0,100 0,93" />
            </svg>
          </div>
        );
      })}
      <div
        className={cx({
          [classes.noDataNotice]: true,
          [classes.hidden]: isLoading,
        })}
      >
        {t("treemap.no_data_notice")}
      </div>
    </>
  );

  const parentTag = childrenLevel.current && mouseOverInfo?.parentName && (
    <div
      className={cx({
        [classes.parentTag]: true,
      })}
      style={{
        left: `${parentTagPos.x}px`,
        top: `${parentTagPos.y}px`,
      }}
    >
      {`${dimensionNames[levels[parentLevel]][i18n.language]}: ${mouseOverInfo.parentName}`}
    </div>
  );

  const graph = (
    <div
      className={cx({
        [classes.canvas]: true,
        [classes.hidden]: isLoading,
      })}
      ref={canvasRef}
      onMouseOver={handleCanvasMouseOver}
      onMouseOut={setMouseOutInfo}
    >
      {treemap.map((box, key) => {
        return treemapBox(box, key);
      })}
      {showClickedBox && (
        <div
          className={cx({
            [classes.clickedBox]: true,
          })}
          style={{
            left: `${clickedBox.x}px`,
            top: `${clickedBox.y}px`,
            width: `${clickedBox.width}px`,
            height: `${clickedBox.height}px`,
          }}
        />
      )}
      {selectedBox.display &&
        selectedBox.items &&
        selectedBox.items.map((item, idx) => (
          <div
            key={idx}
            className={cx({
              [classes.selectedBox]: true,
            })}
            style={{
              left: `${item.x}px`,
              top: `${item.y}px`,
              width: `${item.width}px`,
              height: `${item.height}px`,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: `${Math.min(item.height / 2, item.width * 0.8)}px`,
                }}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
            </div>
          </div>
        ))}
      {parentTag}
    </div>
  );

  const mouseOverInfoEl = (
    <div
      className={cx({
        [classes.mouseOverInfo]: true,
        ["hidden"]: !mouseOverInfo || mouseMoving || showClickedBox,
        [classes.hidden]: isLoading,
      })}
      style={{
        left: `${mouseOverPosition.x}px`,
        top: `${mouseOverPosition.y}px`,
      }}
      onMouseOver={(e) => e.preventDefault()}
      ref={mouseOverRef}
    >
      {mouseOverInfo && mouseOverInfo.parentName && (
        <>
          <div>
            {dimensionNames[levels[parentLevel]][i18n.language]}:{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
              })}
            >
              {mouseOverInfo.parentName}
            </span>
          </div>
          <div>
            {valueNames.size}:{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
              })}
            >
              {valueNames.currency} {formatNumber(mouseOverInfo.parentSize)}
              {totalSize.current !== 0 && (
                <span style={{ fontWeight: 400, opacity: 0.8 }}>
                  {" ("}
                  {(
                    (mouseOverInfo.parentSize / totalSize.current) *
                    100
                  ).toFixed(1)}
                  {"%)"}
                </span>
              )}
            </span>
          </div>
          <div>
            {valueNames.color}:{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
                ["negative"]: mouseOverInfo.parentColor < 0,
              })}
            >
              {colorMetric === "per_volume"
                ? valueNames.currency +
                  " " +
                  mouseOverInfo.parentColor.toFixed(2)
                : (100 * mouseOverInfo.parentColor).toFixed(1) + "%"}
            </span>
          </div>
          <br />
        </>
      )}
      {mouseOverInfo && (
        <>
          <div>
            {childrenLevel.current
              ? dimensionNames[levels[childrenLevel.current]][i18n.language]
              : dimensionNames[levels[parentLevel]][i18n.language]}
            :{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
              })}
            >
              {mouseOverInfo.name}
            </span>
          </div>
          <div>
            {valueNames.size}:{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
              })}
            >
              {valueNames.currency} {formatNumber(mouseOverInfo.size)}
              {totalSize.current !== 0 && (
                <span style={{ fontWeight: 400, opacity: 0.8 }}>
                  {" ("}
                  {((mouseOverInfo.size / totalSize.current) * 100).toFixed(1)}
                  {"%)"}
                </span>
              )}
            </span>
          </div>
          <div>
            {valueNames.color}:{" "}
            <span
              className={cx({
                [classes.mouseOverValue]: true,
                ["negative"]: mouseOverInfo.color < 0,
              })}
            >
              {colorMetric === "per_volume"
                ? valueNames.currency + " " + mouseOverInfo.color.toFixed(2)
                : (100 * mouseOverInfo.color).toFixed(1) + "%"}
            </span>
          </div>
        </>
      )}
    </div>
  );

  const valuesOmittedEl = valuesOmitted.current && (
    <div
      className={cx({
        [classes.valuesOmitted]: true,
        [classes.hidden]: isLoading,
      })}
    >
      {t("treemap.values_omitted")}
      <HelpTooltip
        tooltip={<div>{t("treemap.values_omitted_detail")}</div>}
        small
      />
    </div>
  );
  // endregion

  return (
    <div className={classes.wrapper}>
      {spinner}
      {pathDescription}
      {/*{legend}*/}
      {colorLegend}
      {noDataNotice}
      {graph}
      {mouseOverInfoEl}
      {valuesOmittedEl}
    </div>
  );
};

Treemap.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  levels: PropTypes.object.isRequired,
  dimensionNames: PropTypes.object.isRequired,
  setSelection: PropTypes.func.isRequired,
  setPnlSelection: PropTypes.func.isRequired,
  levelsCount: PropTypes.object.isRequired,
  parentLevel: PropTypes.number.isRequired,
  setParentLevel: PropTypes.func.isRequired,
  childrenLevel: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  totalNodes: PropTypes.object.isRequired,
  totalSize: PropTypes.object.isRequired,
  colorMetric: PropTypes.string.isRequired,
  scaleMode: PropTypes.string.isRequired,
  globalStats: PropTypes.object.isRequired,
  localStats: PropTypes.object.isRequired,
  invertColors: PropTypes.bool,
  selectedBox: PropTypes.object.isRequired,
  setSelectedBox: PropTypes.func.isRequired,
  valueNames: PropTypes.object.isRequired,
  showValues: PropTypes.bool,
};

export default Treemap;
